/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { skillsLink } from "../funcs/skillsLink.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SkillLink } from "../models/components/skilllink.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  LinkSkillRequest,
  LinkSkillSecurity,
} from "../models/operations/linkskill.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type LinkSkillMutationVariables = {
  request: LinkSkillRequest;
  security?: LinkSkillSecurity | undefined;
  options?: RequestOptions;
};

export type LinkSkillMutationData = SkillLink;

export type LinkSkillMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * link skills
 *
 * @remarks
 * Link a skill to exactly one MCP surface that serves it over MCP. Repeating the request for the same skill and target updates the version pin or is a no-op. Requires the skills-over-mcp rollout for the organization.
 */
export function useLinkSkillMutation(
  options?: MutationHookOptions<
    LinkSkillMutationData,
    LinkSkillMutationError,
    LinkSkillMutationVariables
  >,
): UseMutationResult<
  LinkSkillMutationData,
  LinkSkillMutationError,
  LinkSkillMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildLinkSkillMutation(client, options),
    ...options,
  });
}

export function mutationKeyLinkSkill(): MutationKey {
  return ["@gram/client", "skills", "link"];
}

export function buildLinkSkillMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: LinkSkillMutationVariables,
  ) => Promise<LinkSkillMutationData>;
} {
  return {
    mutationKey: mutationKeyLinkSkill(),
    mutationFn: function linkSkillMutationFn({
      request,
      security,
      options,
    }): Promise<LinkSkillMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(skillsLink(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
